import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, Trash2, X } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
} from '@/design/components';
import { useMe } from '@/auth/useMe';
import { usePerm } from '@/access/usePerm';
import { formatDate, formatDateTime, timeLeftLabel, toDateKey, weekStart } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { label, schedulePeriodStatus } from '@/lib/i18n/he';
import { cn } from '@/lib/cn';
import { usePeriodImpact, usePeriodMutations, usePeriods } from './api';
import type { Period } from '../api';

const SELECT = 'min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * שבועות הסידור (PRD 5.4.1).
 *
 * אין כאן "פתיחת תקופה": לכל סניף פעיל נפתח חלון שבועות מתגלגל
 * מעצמו (מיגרציה 038). שבוע שנגמר יוצא, שבוע חדש נכנס, ואף אחד לא
 * צריך לזכור. המסך הזה בוחר סניף ונכנס לשבוע.
 */
export function PeriodsPage() {
  const canManage = usePerm('schedule.manage');
  const { me } = useMe();
  const navigate = useNavigate();
  const periods = usePeriods();
  const { removePeriod } = usePeriodMutations('', me?.employeeId);

  const [branchId, setBranchId] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** התקופה שעומדת למחיקה, עד אישור */
  const [deleting, setDeleting] = useState<Period | null>(null);

  /** ראשון של השבוע הנוכחי. ממנו והלאה זה החלון שנפתח מעצמו. */
  const thisSunday = useMemo(() => toDateKey(weekStart()), []);

  const rows = useMemo(() => periods.data ?? [], [periods.data]);

  // הסניפים נגזרים מהתקופות עצמן ולא מרשימת הסניפים: ככה מוצגים רק
  // סניפים שלמנהל יש בהם סידור, בלי בורר שמוביל לרשימה ריקה.
  const branches = useMemo(() => {
    const seen = new Map<string, string>();
    for (const period of rows) {
      if (period.branch_id && !seen.has(period.branch_id)) {
        seen.set(period.branch_id, period.branches?.name ?? '—');
      }
    }
    return [...seen].map(([id, name]) => ({ id, name }));
  }, [rows]);

  const visible = rows.filter((period) => {
    if (branchId && period.branch_id !== branchId) return false;
    if (!showPast && period.end_date < thisSunday) return false;
    return true;
  });

  const pastCount = rows.filter((period) => period.end_date < thisSunday).length;

  if (!canManage) return <Navigate to="/admin" replace />;

  return (
    <AdminPage
      title="סידור עבודה"
      subtitle="השבועות נפתחים מעצמם. בחר/י סניף ושבוע."
      actions={
        branches.length > 1 ? (
          <select
            value={branchId ?? ''}
            onChange={(event) => setBranchId(event.target.value || null)}
            aria-label="סינון לפי סניף"
            className={SELECT}
          >
            <option value="">כל הסניפים</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        ) : undefined
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <Panel bodyClassName="p-0">
        {periods.isPending && (
          <div className="grid gap-2 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {periods.isError && (
          <div className="p-4">
            <ErrorState onRetry={() => void periods.refetch()} />
          </div>
        )}

        {!periods.isPending && !periods.isError && visible.length === 0 && (
          <div className="p-4">
            <EmptyState
              title="אין שבועות להצגה"
              description={
                branchId
                  ? 'אין שבועות פתוחים בסניף הזה.'
                  : 'השבועות נפתחים אוטומטית. אם המסך ריק, ייתכן שאין סניף פעיל.'
              }
            />
          </div>
        )}

        {visible.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>שבוע</Th>
                <Th>סניף</Th>
                <Th>דדליין אילוצים</Th>
                <Th>סטטוס</Th>
                <Th className="text-end">פעולות</Th>
              </tr>
            </thead>
            <tbody>
              {visible.map((period) => {
                // שבוע בתוך החלון נפתח מחדש מעצמו, ולכן מחיקה שלו רק
                // נראית כאילו עבדה. מוצגת רק על שבועות שכבר עברו.
                const isPast = period.end_date < thisSunday;

                return (
                  <Tr key={period.id} onClick={() => navigate(`/admin/schedule/${period.id}`)}>
                    <Td className="whitespace-nowrap font-medium tabular">
                      {formatDate(period.start_date)} — {formatDate(period.end_date)}
                    </Td>
                    <Td className="text-muted">{period.branches?.name ?? '—'}</Td>
                    <Td className="text-muted">
                      <span className="tabular">{formatDateTime(period.constraints_deadline)}</span>
                      {period.status !== 'published' && (
                        <span className="ms-2 text-label">
                          ({timeLeftLabel(period.constraints_deadline)})
                        </span>
                      )}
                    </Td>
                    <Td>
                      <StatusBadge tone={period.status === 'published' ? 'good' : 'warn'}>
                        {label(schedulePeriodStatus, period.status)}
                      </StatusBadge>
                    </Td>
                    <Td className="whitespace-nowrap text-end">
                      {isPast && (
                        <button
                          type="button"
                          aria-label={`מחיקת השבוע ${formatDate(period.start_date)}`}
                          title="מחיקה"
                          onClick={(event) => {
                            event.stopPropagation();
                            setError(null);
                            setDeleting(period);
                          }}
                          className="inline-flex size-11 cursor-pointer items-center justify-center rounded-sm bg-transparent text-ink hover:bg-cream"
                        >
                          <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                        </button>
                      )}
                      <ChevronLeft
                        size={20}
                        strokeWidth={1.75}
                        aria-hidden="true"
                        className="inline-block text-muted"
                      />
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Panel>

      {pastCount > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowPast((value) => !value)}
            className="min-h-11 cursor-pointer bg-transparent text-body-sm text-ink underline"
          >
            {showPast ? 'להסתיר שבועות שעברו' : `להציג גם שבועות שעברו (${pastCount})`}
          </button>
        </div>
      )}

      {deleting && (
        <DeletePeriodDialog
          period={deleting}
          onClose={() => setDeleting(null)}
          onConfirm={() =>
            removePeriod.mutate(deleting.id, {
              onSuccess: () => setDeleting(null),
              onError: (caught) => {
                setError(errorMessage(caught));
                setDeleting(null);
              },
            })
          }
          isPending={removePeriod.isPending}
        />
      )}
    </AdminPage>
  );
}
function DeletePeriodDialog({
  period,
  onClose,
  onConfirm,
  isPending,
}: {
  period: { id: string; start_date: string; end_date: string; status: string };
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  const impact = usePeriodImpact(period.id);
  const isPublished = period.status === 'published';
  const losses = impact.data
    ? [
        impact.data.constraints > 0 && `${impact.data.constraints} אילוצים שעובדים הגישו`,
        impact.data.submissions > 0 && `${impact.data.submissions} הגשות`,
        impact.data.assignments > 0 && `${impact.data.assignments} שיבוצים`,
      ].filter(Boolean)
    : [];

  return (
    <div className="fixed inset-0 z-30 grid items-end bg-charcoal/40 p-0 sm:place-items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="מחיקת תקופה"
        className="grid max-h-[85dvh] w-full gap-3 overflow-y-auto rounded-t-lg bg-surface p-4 sm:max-w-md sm:rounded-lg"
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="m-0 font-display text-display-m font-semibold">מחיקת תקופה</h2>
          <button
            type="button"
            aria-label="סגירה"
            onClick={onClose}
            className="grid size-11 cursor-pointer place-items-center bg-transparent text-ink"
          >
            <X size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>

        <p className="m-0 text-body text-ink">
          {formatDate(period.start_date)} — {formatDate(period.end_date)}
        </p>

        {impact.isPending && <Skeleton className="h-16 w-full" />}

        {impact.data && (
          <div
            className={cn(
              'grid gap-2 rounded-md border p-3',
              losses.length > 0 ? 'border-bad' : 'border-line',
            )}
          >
            {losses.length === 0 ? (
              <p className="m-0 text-body-sm text-muted">
                אין בתקופה הזו אילוצים או שיבוצים. המחיקה לא תאבד שום נתון.
              </p>
            ) : (
              <>
                <p className="m-0 flex items-center gap-1 text-body-sm font-medium text-bad">
                  <AlertTriangle size={16} strokeWidth={1.75} aria-hidden="true" />
                  יימחקו גם:
                </p>
                <ul className="m-0 grid list-disc gap-1 ps-5 text-body-sm text-ink">
                  {losses.map((line) => (
                    <li key={line as string}>{line}</li>
                  ))}
                </ul>
                <p className="m-0 text-body-sm text-muted">אי אפשר לשחזר את זה.</p>
              </>
            )}

            {isPublished && (
              <p className="m-0 text-body-sm text-bad">
                הסידור הזה כבר פורסם. עובדים ראו אותו והוא ייעלם להם מהאפליקציה.
              </p>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <Button variant="primary" disabled={isPending || impact.isPending} onClick={onConfirm}>
            <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
            {losses.length > 0 ? 'מחיקה על כל הנתונים' : 'מחיקת התקופה'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            ביטול
          </Button>
        </div>
      </div>
    </div>
  );
}
